import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscDashboard,
  BscRes,
  BscResumenArea,
  CorteBsc,
  EstadoEntregaBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { guardarDescarga, mensajeDeDescarga } from "../bsc-descarga";
import { ENTREGA, ESTATUS_PLANEACION, ESTATUS_TRIMESTRE, EstadoBsc, SEMAFORO } from "../bsc-estados";
import { SIN_DATO, fechaCorta, nombres, numero, pct, pts } from "../bsc-formato";
import { AcumuladoQBscComponent } from "../graficas/acumulado-q-bsc.component";
import { BarraAreaBsc, BarrasAreaBscComponent } from "../graficas/barras-area-bsc.component";
import { CumplimientoAreaQBscComponent, SerieAreaQBsc } from "../graficas/cumplimiento-area-q-bsc.component";
import { PromedioAnualAreaBscComponent } from "../graficas/promedio-anual-area-bsc.component";
import { opcionesGraficas, guardarGraficas, OpcionGraficaBsc } from "./visibilidad-graficas-bsc";
import { PastelGerencialBscComponent } from "../graficas/pastel-gerencial-bsc.component";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";
import { TileBsc, TilesBscComponent, avance, claseBarra, claseNota, claseTile } from "../tiles/tiles-bsc.component";

/**
 * Qué se puede enseñar del año al corte:
 *   DATOS            tiles, gráficas y tabla;
 *   SIN_LECTURA      no se pudo consultar el ciclo o las planeaciones: no se afirma que
 *                    no existan (ni se ofrece darlos de alta), solo los avisos;
 *   SIN_CICLO        el año no está dado de alta: sin tabla;
 *   SIN_PLANEACIONES ninguna planeación AUTORIZADA: la tabla dice en qué va cada área;
 *   SIN_CORTE        corte Anual sin ningún trimestre autorizado: la tabla sin trimestres.
 */
type EstadoTablero = "DATOS" | "SIN_LECTURA" | "SIN_CICLO" | "SIN_PLANEACIONES" | "SIN_CORTE";

/**
 * Un renglón de la tabla de áreas, ya armado para pintarse. Se arma UNA vez
 * por respuesta (no en cada detección de cambios), como las filas de T1.
 */
interface FilaArea {
  idBscArea: number | null;
  area: string;
  /** Debajo del nombre: la planeación cuando no está autorizada o es importada. */
  nota: string;
  kpis: string;
  kpisNota: string;
  /** Alcance de Q1..Q(corte − 1). */
  alcancesQ: string[];
  alcance: string;
  programado: string;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  entrega: EstadoBsc<EstadoEntregaBsc> | null;
  /** Debajo de la entrega: el estatus del trimestre cuando dice algo más. */
  entregaNota: string;
  envio: string;
  limite: string;
  /** Sin planeación: se ofrece cargar su FOR-011. */
  cargar: boolean;
  etiquetaVer: string;
}

/** Los cortes que acepta dashboard.do, para leer el de la URL. */
const CORTES = new Set<CorteBsc>(["1", "2", "3", "4", "ANUAL"]);

/** Rutas de las otras pantallas del módulo (hojas de adm_menu admin/bsc/*). */
const RUTA = {
  cargaExcel: "/admin/bsc/carga-excel",
  comparativo: "/admin/bsc/comparativo",
  revision: "/admin/bsc/revision",
  parametros: "/admin/bsc/parametros",
  planeacion: "/admin/bsc/planeacion",
  area: "/admin/bsc/tablero/area",
};

/**
 * BSC · A1 Tablero (docs/BSC.md §7 y Anexo B, maqueta «Main»): la entrada
 * del módulo. Por año y corte (Q1..Q4 o Anual) enseña el alcance de la
 * empresa contra lo programado y contra el objetivo Solser, las áreas por
 * semáforo, las entregas del trimestre, las cinco gráficas del FOR-011 y la
 * tabla de áreas con su entrega, que lleva al detalle del área (A2) y a la carga del FOR-011 (T1).
 *
 * Solo lectura: basta la hoja raíz admin/bsc (dashboard.do, BscConsultaBuss).
 * El servidor ya decide snapshot o en vivo, el corte Anual, las entregas y
 * las fechas nulas ("sin fecha límite"); aquí solo se pinta. Un bloque que no
 * se pudo leer llega en `avisos` y se enseña en una franja, sin tirar lo demás.
 *
 * Base visual: hero y tabla de table-page (src/styles.css) con los tiles
 * de app-tiles-bsc (.tickets-kpis/.ticket-kpi, compartidos con A2 desde
 * F4.3) y las tarjetas .tickets-card de dashboard/shared/dashboard-tarjetas.css
 * (no es global: va en styleUrls, como dashini). Lo de las tarjetas va
 * dentro de .tickets-dashboard porque src/temas/tema-contenido.css cuelga de
 * esa clase los colores de oscuro y cristal.
 *
 * El año y el corte viajan en la URL (?anio=&corte=), así el detalle del área
 * puede regresar al mismo corte.
 *
 * Exportar FOR-011 (F4.4) baja el consolidado del año al corte elegido
 * (exportarConsolidado.do): "Indicadores por Área" y una hoja BSC por área
 * con planeación. Sale con la bandera puedeExportarConsolidado (tablero,
 * revisión o parámetros) y si el año tiene alguna planeación.
 */
@Component({
  selector: "app-tablero-bsc",
  standalone: true,
  templateUrl: "./tablero-bsc.component.html",
  styleUrls: [
    "../../dashboard/shared/dashboard-tarjetas.css",
    "../bsc.shared.css",
    "./tablero-bsc.component.css",
  ],
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    SelectorCorteBscComponent,
    TilesBscComponent,
    BarrasAreaBscComponent,
    PastelGerencialBscComponent,
    AcumuladoQBscComponent,
    CumplimientoAreaQBscComponent,
    PromedioAnualAreaBscComponent,
  ],
})
export class TableroBscComponent implements OnInit, OnDestroy {
  readonly ruta = RUTA;

  /** Lo que viene en la URL, para el selector. */
  anioInicial: number | null = null;
  corteInicial: CorteBsc | null = null;

  anio: number | null = null;
  corte: CorteBsc | null = null;
  loading = false;
  /** Lo que impidió consultar (sin sesión, sin permiso, sin conexión). */
  error = "";

  dashboard: BscDashboard | null = null;
  estado: EstadoTablero = "DATOS";
  /** El trimestre del corte (en Anual, el último autorizado). */
  q: number | null = null;
  tiles: TileBsc[] = [];
  barras: BarraAreaBsc[] = [];
  areasTrimestrales: SerieAreaQBsc[] = [];
  barrasAnuales: BarraAreaBsc[] = [];
  notaAnual = "";
  readonly graficas = opcionesGraficas();

  visible(id: string): boolean {
    return this.graficas.find((grafica) => grafica.id === id)?.visible ?? true;
  }

  alternarGrafica(grafica: OpcionGraficaBsc): void {
    grafica.visible = !grafica.visible;
    guardarGraficas(this.graficas);
  }
  /** Áreas sin alcance al corte, que no salen en las barras. */
  areasSinBarra = 0;
  filas: FilaArea[] = [];
  /** Los trimestres antes del corte, cada uno con su columna. */
  columnasQ: number[] = [];
  avisos: string[] = [];

  puedeCargarExcel = false;
  puedeConfigurar = false;
  puedePlanear = false;
  puedeComparar = false;
  puedeRevisar = false;
  /** Exportar FOR-011: la bandera del servidor y que el año tenga alguna planeación. */
  puedeExportar = false;
  exportando = false;
  /** Lo que impidió bajar el FOR-011 (sin permiso, nada que exportar, sin conexión). */
  errorExportar = "";

  /** La consulta en vuelo: se cancela si el año o el corte cambian antes de que conteste. */
  private consulta?: Subscription;
  private descarga?: Subscription;
  private consultaAnual?: Subscription;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const anio = Number(params.get("anio"));
    this.anioInicial = Number.isInteger(anio) && anio > 1900 && anio < 3000 ? anio : null;
    const corte = (params.get("corte") ?? "").toUpperCase() as CorteBsc;
    this.corteInicial = CORTES.has(corte) ? corte : null;

    // C2 y T2 no traen bandera puede*: se ofrecen si el perfil tiene su hoja.
    const menus = this.authService.getMenuList();
    this.puedeComparar = tieneAccesoMenu(menus, RUTA.comparativo);
    this.puedeRevisar = tieneAccesoMenu(menus, RUTA.revision);
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.descarga?.unsubscribe();
    this.consultaAnual?.unsubscribe();
  }

  /* ============================ Consulta ============================ */

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    this.anio = seleccion.anio;
    this.corte = seleccion.corte ?? "ANUAL";
    this.errorExportar = "";
    // replaceUrl: cambiar de corte no llena el historial del navegador.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { anio: this.anio, corte: this.corte },
      replaceUrl: true,
    });
    this.consultar();
  }

  consultar(): void {
    if (this.anio == null || this.corte == null) {
      return;
    }
    // Solo cuenta la respuesta de lo que sigue elegido.
    const pedido = `${this.anio}|${this.corte}`;
    this.consulta?.unsubscribe();
    this.consultaAnual?.unsubscribe();
    this.barrasAnuales = [];
    this.notaAnual = "Consultando el corte anual…";
    this.loading = true;
    this.error = "";
    this.cdr.detectChanges();

    this.consulta = this.bscService.dashboard(this.anio, this.corte).subscribe({
      next: (res) => {
        if (pedido !== `${this.anio}|${this.corte}`) {
          return;
        }
        this.loading = false;
        if (res?.codigoError) {
          this.error = res.codigoError === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudo consultar el tablero del BSC.";
          this.dashboard = null;
          this.puedeExportar = false;
          this.cdr.detectChanges();
          return;
        }
        this.aplica(res);
        this.consultarAnual();
        this.cdr.detectChanges();
      },
      error: () => {
        if (pedido !== `${this.anio}|${this.corte}`) {
          return;
        }
        this.loading = false;
        this.error = "No se pudo consultar el tablero del BSC: sin conexión con el servidor.";
        this.dashboard = null;
        this.puedeExportar = false;
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: BscRes): void {
    const d: BscDashboard = res.dashboard ?? {};
    this.dashboard = d;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.puedePlanear = res.puedePlanear === true;
    this.q = d.trimestreCorte ?? null;
    this.avisos = d.avisos ?? [];

    const resumen = d.resumenAreas ?? [];
    // Un bloque que falló no es un "no existe": cicloLeido/planeacionesLeidas lo distinguen.
    if (d.cicloLeido === false || (d.estatusCiclo != null && d.planeacionesLeidas === false)) {
      this.estado = "SIN_LECTURA";
    } else if (d.estatusCiclo == null) {
      this.estado = "SIN_CICLO";
    } else if (!resumen.some((r) => r.estatusPlaneacion === "AUTORIZADA")) {
      this.estado = "SIN_PLANEACIONES";
    } else if (this.q == null) {
      this.estado = "SIN_CORTE";
    } else {
      this.estado = "DATOS";
    }

    this.columnasQ = this.q != null ? Array.from({ length: this.q - 1 }, (_, i) => i + 1) : [];
    this.filas = resumen.map((r) => this.fila(r, d));
    this.tiles = this.estado === "DATOS" ? this.armaTiles(d, resumen) : [];
    this.barras = this.barrasDe(resumen);
    this.areasTrimestrales = resumen.map((r) => ({
      nombre: r.area || `Área ${r.idBscArea ?? ""}`.trim(),
      real: Array.from({ length: 4 }, (_, i) => numero(r.alcanceRealQ?.[i])),
    })).filter((area) => area.real.some((valor) => valor != null));
    this.areasSinBarra = resumen.length - this.barras.length;
    // Sin ciclo o sin ninguna planeación el servidor no tiene nada que exportar.
    this.puedeExportar = res.puedeExportarConsolidado === true && d.estatusCiclo != null
      && resumen.some((r) => r.idEjercicio != null);
  }

  private barrasDe(resumen: BscResumenArea[]): BarraAreaBsc[] {
    return resumen.filter((r) => numero(r.alcanceCorte) != null).map((r) => ({
      nombre: r.area || `Área ${r.idBscArea ?? ""}`.trim(),
      real: numero(r.alcanceCorte),
      programado: numero(r.programadoCorte),
      semaforo: r.semaforo ?? null,
    }));
  }

  private consultarAnual(): void {
    if (this.corte === "ANUAL") {
      this.aplicaAnual(this.dashboard);
      return;
    }
    if (this.anio == null || this.estado !== "DATOS") {
      return;
    }
    this.consultaAnual = this.bscService.dashboard(this.anio, "ANUAL").subscribe({
      next: (res) => {
        if (res?.codigoError) {
          this.notaAnual = res.mensajeError ?? "No se pudo consultar el corte anual.";
        } else {
          this.aplicaAnual(res.dashboard ?? null);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.notaAnual = "No se pudo consultar el corte anual: sin conexión con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  private aplicaAnual(dashboard: BscDashboard | null): void {
    this.barrasAnuales = [];
    if (!dashboard || dashboard.cicloLeido === false || dashboard.planeacionesLeidas === false) {
      this.notaAnual = "No se pudo leer el corte anual.";
      return;
    }
    this.barrasAnuales = this.barrasDe(dashboard.resumenAreas ?? []);
    const trimestre = dashboard?.trimestreCorte;
    this.notaAnual = "Sin trimestres autorizados con alcance anual.";
    if (trimestre != null) {
      this.notaAnual = `Anual · último trimestre autorizado Q${trimestre}. No es un promedio de los cuatro acumulados.`;
    }
    const avisos = dashboard?.avisos ?? [];
    if (avisos.length > 0) {
      this.notaAnual += ` ${avisos.join(" ")}`;
    }
  }

  /* ============================ Exportar ============================ */

  /** Baja el FOR-011 consolidado del año al corte que se está viendo. */
  exportar(): void {
    if (this.anio == null || this.exportando) {
      return;
    }
    const anio = this.anio;
    const q = this.q;
    const sufijoQ = q != null ? ` Q${q}` : "";
    this.exportando = true;
    this.errorExportar = "";
    this.cdr.detectChanges();
    this.descarga?.unsubscribe();
    this.descarga = this.bscService.exportarConsolidado(anio, this.corte).subscribe({
      next: (res) => {
        this.exportando = false;
        guardarDescarga(res, `FOR-011 BSC Consolidado ${anio}${sufijoQ}.xlsx`);
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        void mensajeDeDescarga(err, "exportar el FOR-011").then((mensaje) => {
          this.exportando = false;
          this.errorExportar = mensaje;
          this.cdr.detectChanges();
        });
      },
    });
  }

  /* ============================ Encabezado ============================ */

  /** Planeación (T3) abre este año. */
  get queryPlaneacion(): Record<string, number | null> {
    return { anio: this.anio };
  }

  /** T2 (F7.1) de toda la empresa: este año contra el anterior, al mismo corte (Anual = cierre anual). */
  get queryComparativo(): Record<string, string | number | null> {
    const anual = this.corte === "ANUAL";
    return {
      anioA: this.anio != null ? this.anio - 1 : null,
      anioB: this.anio,
      modo: anual ? "cierre" : "corte",
      q: anual ? null : this.corte,
    };
  }

  get titulo(): string {
    return this.anio != null ? `Balanced Scorecard ${this.anio}` : "Balanced Scorecard";
  }

  /** "Alcance acumulado al corte Q2 · 6 áreas · 41 KPIs · 5 sin captura". */
  get resumen(): string {
    const d = this.dashboard;
    if (!d || this.loading || this.error || this.estado !== "DATOS" || this.q == null) {
      return "Alcance de la empresa y de cada área por año y corte, contra lo programado y el objetivo Solser.";
    }
    const partes = [`Alcance acumulado ${this.textoCorte}`];
    if (d.areasConsideradas != null) {
      partes.push(`${d.areasConsideradas} ${d.areasConsideradas === 1 ? "área" : "áreas"}`);
    }
    if (d.totalKpis != null) {
      partes.push(`${d.totalKpis} KPIs`);
    }
    if ((d.kpisSinCaptura ?? 0) > 0) {
      partes.push(`${d.kpisSinCaptura} sin captura`);
    }
    return partes.join(" · ");
  }

  /** "al corte Q2" o "al corte anual (Q4)". */
  get textoCorte(): string {
    if (this.q == null) {
      return this.corte === "ANUAL" ? "al corte anual" : "";
    }
    return this.corte === "ANUAL" ? `al corte anual (Q${this.q})` : `al corte Q${this.q}`;
  }

  /** Lo que se proyecta a la derecha del selector. */
  get textoCiclo(): string {
    const d = this.dashboard;
    if (!d) {
      return "";
    }
    if (d.cicloLeido === false) {
      return "No se pudo leer el año";
    }
    if (d.estatusCiclo == null) {
      return "Sin ciclo";
    }
    if (d.estatusCiclo === "CERRADO") {
      return "Año cerrado";
    }
    return d.origenCiclo === "IMPORTACION_HISTORICA" ? "Histórico importado" : "Año abierto";
  }

  get textoCorteMeta(): string {
    if (!this.dashboard || this.loading) {
      // Lo pedido, mientras contesta el servidor.
      return this.corte === "ANUAL" ? "Anual" : `Corte Q${this.corte}`;
    }
    if (this.q != null) {
      return this.corte === "ANUAL" ? `Anual · hasta Q${this.q}` : `Corte Q${this.q}`;
    }
    if (this.corte !== "ANUAL") {
      return `Corte Q${this.corte}`;
    }
    return this.estado === "SIN_CORTE" ? "Anual · sin trimestres autorizados" : "Anual";
  }

  /* ============================ Tiles ============================ */

  private armaTiles(d: BscDashboard, resumen: BscResumenArea[]): TileBsc[] {
    const q = this.q as number;
    return [this.tileAlcance(d), this.tileBrecha(d, q), this.tileAreas(d), this.tileEntregas(d, resumen, q)];
  }

  private tileAlcance(d: BscDashboard): TileBsc {
    const alcance = numero(d.alcanceEmpresa);
    const programado = numero(d.programadoEmpresa);
    const semaforo = this.semaforo(d.semaforoEmpresa);
    return {
      etiqueta: "Alcance de la empresa",
      valor: pct(alcance),
      meta: `de ${pct(programado)} programado`,
      icono: "insights",
      clase: claseTile(d.semaforoEmpresa),
      nota: semaforo ? semaforo.texto : undefined,
      notaClase: claseNota(d.semaforoEmpresa),
      // La barrita es real contra programado; sin alguno de los dos no se pinta.
      barra: avance(alcance, programado),
      barraClase: claseBarra(d.semaforoEmpresa),
    };
  }

  private tileBrecha(d: BscDashboard, q: number): TileBsc {
    const brecha = numero(d.brecha);
    const anterior = q > 1 ? numero(d.serieBrechaQ?.[q - 2]) : null;
    return {
      etiqueta: "Brecha contra objetivo Solser",
      valor: pts(brecha),
      meta: `objetivo ${pct(d.objetivoSolser)} · ${this.textoCorte}`,
      icono: "flag",
      clase: claseDiferencia(brecha),
      nota: anterior != null ? `En Q${q - 1}: ${pts(anterior)}` : undefined,
    };
  }

  private tileAreas(d: BscDashboard): TileBsc {
    const enMeta = d.areasEnMeta ?? 0;
    const enRiesgo = d.areasEnRiesgo ?? 0;
    const fuera = d.areasFuera ?? 0;
    const total = d.areasConsideradas ?? enMeta + enRiesgo + fuera;
    const enRiesgoDesde = d.semaforoAmarillo != null ? `; en riesgo desde ${pct(d.semaforoAmarillo)}` : "";
    const regla = d.semaforoVerde != null
      ? `En meta desde ${pct(d.semaforoVerde)} de lo programado` + enRiesgoDesde
      : undefined;
    return {
      etiqueta: "Áreas en meta",
      valor: d.areasEnMeta != null ? `${enMeta}` : SIN_DATO,
      meta: `de ${total} · ${enRiesgo} en riesgo · ${fuera} fuera`,
      icono: "traffic",
      clase: claseConteos(fuera, enRiesgo),
      nota: regla,
    };
  }

  private tileEntregas(d: BscDashboard, resumen: BscResumenArea[], q: number): TileBsc {
    const tile: TileBsc = {
      etiqueta: "Entregas del trimestre",
      valor: SIN_DATO,
      meta: "",
      icono: "assignment_turned_in",
      clase: "ticket-kpi--muted",
    };
    if (this.puedeRevisar) {
      // C2 (F6.6) abre en el año y el trimestre del corte.
      tile.enlace = { texto: "Abrir la revisión", ruta: RUTA.revision, queryParams: { anio: this.anio, trimestre: q } };
    }
    if (d.entregasEnviadas == null) {
      tile.meta = "No se pudo leer el estatus de los trimestres";
      return tile;
    }
    const enviadas = d.entregasEnviadas;
    const pendientes = d.entregasPendientes ?? 0;
    const vencidas = d.entregasVencidas ?? 0;
    const esperadas = enviadas + pendientes + vencidas;
    if (esperadas === 0) {
      tile.meta = `Ningún área espera entregar Q${q}`;
      return tile;
    }
    tile.valor = `${enviadas} / ${esperadas}`;
    tile.meta = `${esperadas === 1 ? "área entregó" : "áreas entregaron"} Q${q}`;
    const limite = d.fechaLimiteEnvio ? `límite ${fechaCorta(d.fechaLimiteEnvio)}` : "sin fecha límite";
    const de = (estado: EstadoEntregaBsc) =>
      resumen.filter((r) => r.estadoEntrega === estado).map((r) => r.area || SIN_DATO);
    if (vencidas > 0) {
      tile.clase = "ticket-kpi--total";
      tile.nota = `${nombres(de("VENCIDA"))}: ${vencidas === 1 ? "vencida" : "vencidas"} · ${limite}`;
      tile.notaClase = "is-peligro";
    } else if (pendientes > 0) {
      tile.clase = "ticket-kpi--time";
      tile.nota = `${nombres(de("PENDIENTE"))}: ${pendientes === 1 ? "pendiente" : "pendientes"} · ${limite}`;
      tile.notaClase = "is-aviso";
    } else {
      tile.clase = "ticket-kpi--done";
      tile.nota = "Todas las áreas entregaron";
      tile.notaClase = "is-exito";
    }
    return tile;
  }

  /* ============================ Tabla ============================ */

  private fila(r: BscResumenArea, d: BscDashboard): FilaArea {
    const nombre = r.area || `Área ${r.idBscArea ?? ""}`.trim();
    const conPlaneacion = r.idEjercicio != null;
    const autorizada = r.estatusPlaneacion === "AUTORIZADA";
    const real = r.alcanceRealQ ?? [];
    const sinCaptura = r.kpisSinCaptura ?? 0;
    return {
      idBscArea: r.idBscArea ?? null,
      area: nombre,
      nota: this.notaArea(r, d),
      kpis: r.kpis != null ? `${r.kpis}` : SIN_DATO,
      kpisNota: sinCaptura > 0 ? `${sinCaptura} sin captura` : "",
      alcancesQ: this.columnasQ.map((q) => pct(real[q - 1])),
      alcance: pct(r.alcanceCorte),
      programado: pct(r.programadoCorte),
      semaforo: this.semaforo(r.semaforo),
      // Una planeación en borrador, enviada o regresada no espera entregas: sin insignia (la
      // nota bajo el área ya dice su estatus). "Sin planeación" es solo para el área sin ella.
      entrega: conPlaneacion && !autorizada ? null : ENTREGA.find((e) => e.valor === r.estadoEntrega) ?? null,
      entregaNota: this.notaEntrega(r),
      envio: fechaCorta(r.fechaEnvio),
      // La fecha límite es del ciclo: sin planeación (o sin corte) no aplica.
      limite: autorizada && this.q != null ? fechaCorta(r.fechaLimite, "Sin fecha límite") : SIN_DATO,
      cargar: !conPlaneacion && this.puedeCargarExcel,
      etiquetaVer: `Ver área ${nombre}`,
    };
  }

  /** La planeación del área en una línea, solo si no es la normal (autorizada en el flujo). */
  private notaArea(r: BscResumenArea, d: BscDashboard): string {
    if (r.idEjercicio == null) {
      return `Sin planeación en ${d.anio ?? this.anio ?? ""}`.trim();
    }
    if (r.estatusPlaneacion !== "AUTORIZADA") {
      const estatus = ESTATUS_PLANEACION.find((e) => e.valor === r.estatusPlaneacion);
      return `Planeación: ${estatus ? estatus.texto.toLowerCase() : "sin autorizar"}`;
    }
    if (r.origenPlaneacion === "IMPORTACION_HISTORICA") {
      return "Histórico importado del FOR-011";
    }
    if (r.origenPlaneacion === "IMPORTACION_ARRANQUE") {
      return "Arranque importado del FOR-011";
    }
    return "";
  }

  /** El estatus del trimestre del corte cuando la entrega no lo dice ya. */
  private notaEntrega(r: BscResumenArea): string {
    switch (r.estadoEntrega) {
      case "PENDIENTE":
      case "VENCIDA": {
        const estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === r.estatusTrimestre);
        return estatus ? `Trimestre ${estatus.texto.toLowerCase()}` : "";
      }
      case "SIN_DATOS":
        return "Histórico cargado hasta un trimestre anterior";
      default:
        return "";
    }
  }

  private semaforo(valor: SemaforoBsc | null | undefined): EstadoBsc<SemaforoBsc> | null {
    return SEMAFORO.find((s) => s.valor === valor) ?? null;
  }

  trackFila(_: number, f: FilaArea): string {
    return `${f.idBscArea ?? f.area}`;
  }
}

/** El color del tile por una diferencia en puntos: sin dato, a favor (desde 0) o en contra. */
function claseDiferencia(diferencia: number | null): string {
  if (diferencia == null) {
    return "ticket-kpi--muted";
  }
  return diferencia >= 0 ? "ticket-kpi--done" : "ticket-kpi--total";
}

/** El color del tile por cuántos van fuera o en riesgo: fuera manda sobre en riesgo. */
function claseConteos(fuera: number, enRiesgo: number): string {
  if (fuera > 0) {
    return "ticket-kpi--total";
  }
  return enRiesgo > 0 ? "ticket-kpi--time" : "ticket-kpi--done";
}
